import { useMemo, useState } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { AlertTriangle, RotateCcw } from 'lucide-react'
import { PageHero } from '@components/ui'
import { useMechanicPendingWorkOrders } from '@hooks/useMechanicData'
import { useAuthStore } from '@store/useAuthStore'
import { formatDate, formatStatus } from '@utils/format'
import { isWorkOrderInWorkshop } from '../types/workOrder'
import type { WorkOrderSummary } from '../types/workOrderSummary'

/** Estados em fila de trabalho (cartões por estado). */
const OPEN_WORK_STATUSES = ['DRAFT', 'IN_PROGRESS', 'WAITING_PARTS', 'WAITING_APPROVAL']

const matchesSearch = (workOrder: WorkOrderSummary, term: string) => {
  if (!term) return true
  
  // Extrair marca e modelo do vehicleInfo (formato: "Marca Modelo, Ano")
  const vehicleInfo = workOrder.vehicleInfo || ''
  const vehicleParts = vehicleInfo.split(',').map(p => p.trim())
  const marcaModelo = vehicleParts[0] || '' // "Marca Modelo"
  const marca = marcaModelo.split(' ')[0] || '' // Primeira palavra (marca)
  const modelo = marcaModelo.split(' ').slice(1).join(' ') || '' // Resto (modelo)
  
  const haystack = [
    workOrder.workOrderNumber,
    workOrder.customerCompanyName,
    marca,
    modelo,
    vehicleInfo,
    workOrder.description,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()

  return haystack.includes(term.toLowerCase())
}

export function MechanicWorkOrdersPage() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const [search, setSearch] = useState('')
  const [selectedFilter, setSelectedFilter] = useState<string | null>(null) // null = todos, 'DRAFT' = apenas rascunho, etc.
  const [showOnlyMine, setShowOnlyMine] = useState(false) // Filtro "Minhas" - apenas para mecânicos

  const workshopCompanyId = params.get('workshopId')
    ? Number(params.get('workshopId'))
    : user?.workshopCompanyId

  const isMechanic = user?.functionId === 2 // FunctionId 2 = Mecânico

  const {
    data: workOrders = [],
    isLoading,
    isError,
    refetch,
  } = useMechanicPendingWorkOrders({
    workshopCompanyId,
    mechanicId: undefined, // Não passar mechanicId para buscar TODAS as folhas de obra da oficina
  })

  const searchTerm = search.trim().toLowerCase()

  const filteredWorkOrders = useMemo(
    () => {
      let result = workOrders.filter((order) => isWorkOrderInWorkshop(order.status))

      if (showOnlyMine && isMechanic && user?.id) {
        result = result.filter((order) => order.assignedMechanicId === user.id)
      }

      if (selectedFilter !== null) {
        result = result.filter((order) => order.status === selectedFilter)
      }
      
      // Aplicar busca
      return result.filter((order) => matchesSearch(order, searchTerm))
    },
    [workOrders, searchTerm, selectedFilter, showOnlyMine, isMechanic, user?.id]
  )

  // Calcular contadores baseados em todas as work orders (não filtradas)
  const inWorkshopWorkOrders = useMemo(
    () => workOrders.filter((order) => isWorkOrderInWorkshop(order.status)),
    [workOrders]
  )

  // Calcular contadores para "Minhas" (apenas para mecânicos)
  const myPendingWorkOrders = useMemo(
    () => {
      if (!isMechanic || !user?.id) return []
      return inWorkshopWorkOrders.filter((order) => order.assignedMechanicId === user.id)
    },
    [inWorkshopWorkOrders, isMechanic, user?.id]
  )

  const statusCount = useMemo(() => {
    return inWorkshopWorkOrders.reduce<Record<string, number>>((acc, order) => {
      acc[order.status] = (acc[order.status] ?? 0) + 1
      return acc
    }, {})
  }, [inWorkshopWorkOrders])

  const totalPending = inWorkshopWorkOrders.length
  const myTotalPending = myPendingWorkOrders.length

  // Função para gerar mensagem dinâmica baseada no filtro
  const getEmptyStateMessage = () => {
    if (selectedFilter === null) {
      return {
        title: 'Não há folhas de obra na oficina',
        description: 'Quando existirem veículos em atendimento, aparecerão aqui.',
      }
    }
    
    const statusLabel = formatStatus(selectedFilter)
    return {
      title: `Nenhuma folha de obra com status "${statusLabel}" encontrada.`,
      description: `Ajuste os filtros ou verifique se existem folhas no estado ${statusLabel.toLowerCase()} para esta oficina.`,
    }
  }

  const emptyStateMessage = getEmptyStateMessage()

  return (
    <div className="space-y-8">
      <PageHero
        title="FOLHAS DE OBRA"
        searchPlaceholder="Pesquisar por número da folha, cliente ou veículo..."
        searchValue={search}
        onSearchChange={setSearch}
      />

      {!workshopCompanyId ? (
        <div className="rounded-3xl border border-yellow-200 bg-yellow-50 p-8 text-yellow-800">
          <div className="flex items-center gap-3 font-semibold">
            <AlertTriangle className="h-5 w-5" />
            É necessário configurar a oficina do mecânico para carregar as folhas de obra.
          </div>
          <p className="mt-2 text-sm">
            Informe o parâmetro <code>workshopId</code> na URL ou associe o mecânico a uma oficina.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex overflow-x-auto overflow-y-hidden scrollbar-hide gap-2 md:gap-4 flex-nowrap md:flex-wrap pb-1 -mx-1 md:mx-0 md:pb-0">
            <div
              role="button"
              tabIndex={0}
              onClick={() => {
                console.log('Clicked Pendentes')
                setSelectedFilter(null)
                setShowOnlyMine(false)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setSelectedFilter(null)
                  setShowOnlyMine(false)
                }
              }}
              className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 cursor-pointer transition-all select-none flex items-center justify-between gap-2 md:gap-4 ${
                selectedFilter === null && !showOnlyMine
                  ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                  : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
              } flex-shrink-0`}
            >
              <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
                selectedFilter === null && !showOnlyMine ? 'text-white/70' : 'text-secondary-400'
              }`}>
                Pendentes
              </p>
              <p className={`text-sm md:text-lg font-bold ${
                selectedFilter === null && !showOnlyMine ? 'text-white' : 'text-secondary-900'
              }`}>
                {totalPending}
              </p>
            </div>
            {/* Filtro "Minhas" - apenas para mecânicos */}
            {isMechanic && (
              <div
                role="button"
                tabIndex={0}
                onClick={() => {
                  console.log('Clicked Minhas')
                  setSelectedFilter(null)
                  setShowOnlyMine(!showOnlyMine)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedFilter(null)
                    setShowOnlyMine(!showOnlyMine)
                  }
                }}
                className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 cursor-pointer transition-all select-none flex items-center justify-between gap-2 md:gap-4 ${
                  showOnlyMine && selectedFilter === null
                    ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                    : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
                } flex-shrink-0`}
              >
                <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
                  showOnlyMine && selectedFilter === null ? 'text-white/70' : 'text-secondary-400'
                }`}>
                  Minhas
                </p>
                <p className={`text-sm md:text-lg font-bold ${
                  showOnlyMine && selectedFilter === null ? 'text-white' : 'text-secondary-900'
                }`}>
                  {myTotalPending}
                </p>
              </div>
            )}
            {OPEN_WORK_STATUSES.filter((status) => (statusCount[status] ?? 0) > 0).map((status) => (
              <div
                key={status}
                role="button"
                tabIndex={0}
                onClick={() => {
                  console.log('Clicked status:', status)
                  setSelectedFilter(status)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setSelectedFilter(status)
                  }
                }}
                className={`rounded-2xl md:rounded-3xl border px-4 py-3 md:px-6 md:py-4 cursor-pointer transition-all select-none flex items-center justify-between gap-2 md:gap-4 ${
                  selectedFilter === status
                    ? 'border-[#17253C] bg-[#17253C] text-white shadow-md'
                    : 'border-secondary-200 bg-white hover:border-secondary-300 hover:shadow-sm'
                } flex-shrink-0`}
              >
                <p className={`text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] md:tracking-[0.3em] ${
                  selectedFilter === status ? 'text-white/70' : 'text-secondary-400'
                }`}>
                  {formatStatus(status)}
                </p>
                <p className={`text-sm md:text-lg font-bold ${
                  selectedFilter === status ? 'text-white' : 'text-secondary-900'
                }`}>
                  {statusCount[status] ?? 0}
                </p>
              </div>
            ))}
            <button
              type="button"
              onClick={() => refetch()}
              className="inline-flex items-center justify-center rounded-full border border-secondary-200 px-4 py-2 text-sm font-semibold text-secondary-600 transition hover:border-secondary-300 hover:text-secondary-800 flex-shrink-0"
            >
              <RotateCcw className="h-4 w-4" />
              <span className="ml-2">Recarregar</span>
            </button>
          </div>

          {isError ? (
            <div className="rounded-3xl border border-red-200 bg-red-50 p-8 text-red-700">
              Não foi possível carregar as folhas de obra. Tente novamente.
            </div>
          ) : isLoading ? (
            <div className="grid min-h-[200px] place-items-center rounded-3xl border border-secondary-200 bg-white">
              <p className="text-secondary-500">A carregar folhas de obra pendentes...</p>
            </div>
          ) : filteredWorkOrders.length === 0 ? (
            <div className="rounded-3xl border border-secondary-200 bg-white p-10 text-center">
              <p className="text-lg font-semibold text-secondary-700">
                {emptyStateMessage.title}
              </p>
              <p className="text-secondary-500">
                {emptyStateMessage.description}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid gap-2 sm:gap-4 grid-cols-2 sm:grid-cols-2 xl:grid-cols-3 max-h-[min(22rem,58vh)] md:max-h-none overflow-y-auto md:overflow-visible scrollbar-hide">
                {filteredWorkOrders.map((order) => {
                  return (
                    <button
                      key={order.id}
                      type="button"
                      onClick={() => navigate(`/work-orders/${order.id}`)}
                      className="text-left"
                      title="Clique para ver detalhes"
                    >
                      <div className="rounded-2xl md:rounded-3xl border border-secondary-200 bg-white px-3 py-3 md:px-5 md:py-4 transition-all hover:border-secondary-300 hover:shadow-md">
                        <div className="flex items-center justify-between gap-1">
                          <span className="text-xs md:text-sm font-semibold text-secondary-800 truncate">
                            {order.workOrderNumber}
                          </span>
                          <span className="text-[10px] md:text-xs font-semibold text-secondary-500 flex-shrink-0">
                            {formatStatus(order.status)}
                          </span>
                        </div>
                        <div className="mt-1 md:mt-2 text-xs md:text-sm text-secondary-700 line-clamp-1">
                          {order.customerCompanyName ?? 'Cliente não definido'}
                        </div>
                        {order.vehicleInfo && (
                          <div className="mt-0.5 md:mt-1 text-[10px] md:text-xs text-secondary-500 line-clamp-1">{order.vehicleInfo}</div>
                        )}
                        {order.createdAt && (
                          <div className="mt-1 md:mt-2 text-[10px] md:text-xs text-secondary-400">
                            Criada em {formatDate(order.createdAt)}
                          </div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}




